import { FC } from 'react'
import { TSettingGroup } from '../../../model/ISettings'
import useSettings, { ISettingsState } from '../../hooks/useSettings'
import Button from '../form/Button'
import SettingFormGroup from './SettingFormGroup'
import FormBlock from '../form/FormBlock'
import ButtonLink from '../form/buttons/ButtonLink'
import useImport from '../../hooks/useImport'
import { useConfirm } from '../../hooks/useConfirm'

const groupTitles: Record<TSettingGroup, { title: string; description: string }> = {
  common: {
    title: 'Common Settings',
    description: 'Configure the general settings of the application.',
  },
  cron: {
    title: 'Automatic updates schedule',
    description:
      'Configure the parameters for automatic product updates. Please note that manual import will not work when processing is disabled.',
  },
  import: {
    title: 'Manual Product Import',
    description:
      'Configure which product parameters will be updated during manual import. New products will be uploaded in full.',
  },
  update: {
    title: 'Product Update',
    description:
      'Configure which product parameters will be updated when the products are automatically updated.',
  },
}

const SettingForm: FC<{ defaultState: ISettingsState }> = ({ defaultState }) => {
  const { state, handleUpdate, handleReset, handleSubmit, loading } = useSettings(defaultState)
  const { deletePendingImportsHandler, deleteAllImportsHandler } = useImport()
  const confirm = useConfirm()

  return (
    <div className='space-y-12'>
      {Object.entries(state).map(([key, value]) => (
        <SettingFormGroup
          key={key}
          settingGroupState={value}
          title={groupTitles?.[key as TSettingGroup]?.title || ''}
          description={groupTitles?.[key as TSettingGroup]?.description || ''}
          handleUpdate={handleUpdate}
        />
      ))}

      <FormBlock
        title='Actions'
        subTitle='Actions with the queue for updating and importing products'
        className='col-span-12 grid grid-cols-12 gap-6  p-4'
      >
        <div className='col-span-6'>
          <ButtonLink
            onClick={() =>
              confirm.on({
                text: (
                  <div className='text-xl'>
                    Please confirm that you want to delete Pending imports
                  </div>
                ),
                action: deletePendingImportsHandler,
                applyText: 'Delete',
              })
            }
            variant='delete'
          >
            Delete pending imports
          </ButtonLink>
        </div>

        <div className='col-span-6'>
          <ButtonLink
            onClick={() =>
              confirm.on({
                text: (
                  <div className='text-xl'>Please confirm that you want to delete ALL imports</div>
                ),
                action: deleteAllImportsHandler,
                applyText: 'Delete',
              })
            }
            variant='delete'
          >
            Delete all imports
          </ButtonLink>
        </div>
      </FormBlock>

      <div className='fixed bottom-0 right-0 w-[100%] bg-white px-8 py-6 border-t-[1px]'>
        <div className='flex justify-end gap-4 '>
          <Button
            isNotFullWidth
            onClick={handleReset}
            disabled={loading}
          >
            Reset
          </Button>
          <Button
            isNotFullWidth
            type='submit'
            onClick={handleSubmit}
            disabled={loading}
          >
            Save
          </Button>
        </div>
      </div>
      <div className='h-[3em]'></div>
    </div>
  )
}

export default SettingForm
